import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { helpdeskAPI } from '../services/api';
import './TicketList.css';

function TicketList() {
    const { t, language } = useLanguage();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ status: '', priority: '' });

    useEffect(() => {
        loadTickets();
    }, [filter]);

    const loadTickets = async () => {
        try {
            setLoading(true);
            const response = await helpdeskAPI.getTickets(filter);
            if (response.data.success) {
                setTickets(response.data.tickets);
            }
        } catch (error) {
            console.error('Failed to load tickets:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'open': { label: t('statusOpen'), className: 'open' },
            'assigned': { label: t('statusAssigned'), className: 'assigned' },
            'in_progress': { label: t('statusInProgress'), className: 'in-progress' },
            'waiting_for_customer': { label: t('statusWaiting'), className: 'awaiting' },
            'resolved': { label: t('statusResolved'), className: 'resolved' },
            'closed': { label: t('statusClosed'), className: 'closed' }
        };
        const s = statusMap[status] || { label: status, className: 'default' };
        return <span className={`status-badge ${s.className}`}>{s.label}</span>;
    };

    const getPriorityBadge = (priority) => {
        const priorityMap = {
            'low': { label: t('priorityLow'), className: 'low' },
            'medium': { label: t('priorityMedium'), className: 'medium' },
            'high': { label: t('priorityHigh'), className: 'high' },
            'critical': { label: t('priorityCritical'), className: 'critical' }
        };
        const p = priorityMap[priority] || { label: priority, className: 'default' };
        return <span className={`priority-badge ${p.className}`}>{p.label}</span>;
    };

    if (loading) {
        return <div className="loading">{t('loading')}</div>;
    }

    return (
        <div className="ticket-list-page">
            <div className="page-header">
                <div>
                    <h1>{t('myTickets')}</h1>
                    <p>{language === 'ar' ? 'عرض وإدارة جميع تذاكر الدعم الخاصة بك' : 'View and manage all your support tickets'}</p>
                </div>
                <Link to="/tickets/new" className="new-ticket-btn">+ {t('newTicket')}</Link>
            </div>

            <div className="tickets-container">
                <div className="filters-bar">
                    <span>{tickets.length} {language === 'ar' ? 'تذكرة' : 'tickets'}</span>
                    <div className="filters">
                        <select 
                            value={filter.status} 
                            onChange={(e) => setFilter({...filter, status: e.target.value})}
                        >
                            <option value="">{language === 'ar' ? 'جميع الحالات' : 'All Status'}</option>
                            <option value="open">{t('statusOpen')}</option>
                            <option value="in_progress">{t('statusInProgress')}</option>
                            <option value="resolved">{t('statusResolved')}</option>
                            <option value="closed">{t('statusClosed')}</option>
                        </select>
                        <select 
                            value={filter.priority} 
                            onChange={(e) => setFilter({...filter, priority: e.target.value})}
                        >
                            <option value="">{language === 'ar' ? 'جميع الأولويات' : 'All Priority'}</option>
                            <option value="low">{t('priorityLow')}</option>
                            <option value="medium">{t('priorityMedium')}</option>
                            <option value="high">{t('priorityHigh')}</option>
                            <option value="critical">{t('priorityCritical')}</option>
                        </select>
                    </div>
                </div>

                {tickets.length === 0 ? (
                    <div className="empty-state">
                        <h3>{t('noTicketsYet')}</h3>
                        <p>{t('createFirstTicket')}</p>
                        <Link to="/tickets/new" className="create-btn">{t('createTicket')}</Link>
                    </div>
                ) : (
                    <div className="tickets-grid">
                        {tickets.map(ticket => (
                            <Link to={`/tickets/${ticket.id}`} key={ticket.id} className="ticket-card">
                                <div className="ticket-card-header">
                                    <span className="ticket-id">#{ticket.ticket_number}</span>
                                    {getStatusBadge(ticket.status)}
                                </div>
                                <h3 className="ticket-subject">{ticket.subject}</h3>
                                <div className="ticket-meta">
                                    {getPriorityBadge(ticket.priority)}
                                    <span className="ticket-category">{ticket.category_name || (language === 'ar' ? 'عام' : 'General')}</span>
                                    {ticket.company_name && (
                                        <span className="ticket-company">🏢 {ticket.company_name}</span>
                                    )}
                                </div>
                                <div className="ticket-footer">
                                    <span>📅 {formatDate(ticket.created_at)}</span>
                                    <span>💬 {ticket.reply_count || 0} {language === 'ar' ? 'رد' : 'replies'}</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default TicketList;
